import React, { useState, useEffect } from 'react';
import { 
    CreditCard, CheckCircle, AlertTriangle, Search, DollarSign, FileText, ArrowRight, ChevronDown, ChevronUp, Info 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const EncaissementHebergement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('encaissements');
  const [paiements, setPaiements] = useState([]);
  const [sejours, setSejours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Gestion de l'accordéon et du détail chargé à la volée
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resPaiements, resReservations] = await Promise.allSettled([
        API.get('/hebergement/reservations/encaissements').catch(() => ({ data: [] })),
        API.get('/hebergement/reservations').catch(() => ({ data: [] }))
      ]);

      if (resPaiements.status === 'fulfilled') {
        setPaiements(resPaiements.value.data || []);
      }
      if (resReservations.status === 'fulfilled') {
        setSejours(resReservations.value.data || []);
      }
    } catch (err) {
      console.error("Erreur chargement des données:", err);
      showToast("Erreur de chargement des données financières", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredPaiements = Array.isArray(paiements) ? paiements.filter(p => 
    (p.reservant_nom && p.reservant_nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.reference && p.reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.mode_paiement && p.mode_paiement.toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  const filteredSejours = Array.isArray(sejours) ? sejours.filter(s => 
    (s.reservant_nom && s.reservant_nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.client_principal_nom && s.client_principal_nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.id && String(s.id).toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  const toggleRow = async (p) => {
    const rowId = p.id;
    if (expandedRowId === rowId) {
      setExpandedRowId(null);
      setDetailData(null);
      return;
    }

    setExpandedRowId(rowId);
    setDetailData(null);
    setLoadingDetail(true);

    try {
      const res = await API.get(`/hebergement/reservations/encaissements/${p.id}/details`);
      setDetailData(res.data);
    } catch (err) {
      console.error("Erreur chargement détails encaissement:", err);
      showToast("Impossible de charger les détails ventilés", "error");
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CreditCard size={22} color="#2563eb" /> ENCAISSEMENTS & FACTURATION HÉBERGEMENT
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Suivi global des règlements, factures soldées et impayés
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => setActiveTab('encaissements')}
              style={{
                padding: '10px 16px', borderRadius: '8px', border: 'none', fontWeight: '800', fontSize: '13px', cursor: 'pointer',
                background: activeTab === 'encaissements' ? '#2563eb' : '#e2e8f0',
                color: activeTab === 'encaissements' ? 'white' : '#475569',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <DollarSign size={16} /> Historique Encaissements
            </button>
            <button 
              onClick={() => setActiveTab('factures')}
              style={{
                padding: '10px 16px', borderRadius: '8px', border: 'none', fontWeight: '800', fontSize: '13px', cursor: 'pointer',
                background: activeTab === 'factures' ? '#2563eb' : '#e2e8f0',
                color: activeTab === 'factures' ? 'white' : '#475569',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <FileText size={16} /> Suivi Factures & Impayés
            </button>
          </div>
        </header>

        <div style={{ padding: '30px 40px', flex: 1 }}>

          <div style={{ ...contentCardStyle, padding: '15px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Search size={18} color="#64748b" />
            <input 
              type="text" 
              style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px', background: 'transparent' }} 
              placeholder={activeTab === 'encaissements' ? "Rechercher par nom, mode ou référence..." : "Rechercher un dossier, un client..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {activeTab === 'encaissements' && (
            <div style={contentCardStyle}>
              <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Info size={14} /> Cliquez sur une ligne d'encaissement pour afficher le détail complet de la réservation et des trois catégories (Hébergement, Restauration, Extras).
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={thStyle}>Date & Heure</th>
                    <th style={thStyle}>Dossier / Réservant</th>
                    <th style={thStyle}>Mode de Paiement</th>
                    <th style={thStyle}>Référence</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Montant Encaissé</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Chargement en cours...</td></tr>
                  ) : filteredPaiements.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Aucun encaissement trouvé.</td></tr>
                  ) : (
                    filteredPaiements.map((p, idx) => {
                      const rowId = p.id || idx;
                      const isExpanded = expandedRowId === rowId;

                      const mHebergement = Number(p.montant_hebergement || 0);
                      const mRestauration = Number(p.montant_restauration || 0);
                      const mExtras = Number(p.montant_services_extras || 0);

                      // Calcul du montant total réel basé sur la somme des ventilations ou fallback sur p.montant
                      const totalReelEncaisse = mHebergement + mRestauration + mExtras;
                      const montantLigneFinal = totalReelEncaisse > 0 ? totalReelEncaisse : Number(p.montant || 0);

                      return (
                        <React.Fragment key={rowId}>
                          <tr 
                            onClick={() => toggleRow(p)}
                            style={{ 
                              borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', 
                              cursor: 'pointer', 
                              background: isExpanded ? '#f8fafc' : 'transparent',
                              transition: 'background 0.2s' 
                            }}
                            onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isExpanded ? <ChevronUp size={16} color="#2563eb" /> : <ChevronDown size={16} color="#64748b" />}
                                {p.date_paiement ? new Date(p.date_paiement).toLocaleString('fr-FR') : '---'}
                              </div>
                            </td>
                            <td style={{ ...tdStyle, fontWeight: '800' }}>
                              {p.reservant_nom || 'Client direct'} 
                              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>Ref Groupe: #{p.groupe_id}</div>
                            </td>
                            <td style={tdStyle}>
                              <span style={badgeStyle(p.mode_paiement)}>{p.mode_paiement || 'ESPECES'}</span>
                            </td>
                            <td style={tdStyle}>{p.reference || '---'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '800', color: '#10b981' }}>
                              {montantLigneFinal.toLocaleString()} F
                            </td>
                          </tr>

                          {/* ACCORDÉON / TIROIR DÉPLOYÉ EN BAS DE LA LIGNE */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={5} style={{ background: '#f8fafc', padding: '20px 30px', borderBottom: '2px solid #e2e8f0', boxShadow: 'inset 0 4px 6px -4px rgba(0,0,0,0.05)' }}>
                                <div style={{ maxWidth: '850px', margin: '0 auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                                  
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>
                                      Détail ventilé du paiement et de la réservation #{p.groupe_id}
                                    </div>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                                      Montant de ce reçu : <strong style={{ color: '#10b981' }}>{montantLigneFinal.toLocaleString()} F</strong>
                                    </span>
                                  </div>

                                  {/* Ventilation résumée de l'encaissement */}
                                  <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                                    {mHebergement > 0 && <span style={badgeVentilation('#2563eb', '#eff6ff')}>🛏️ Hébergement : {mHebergement.toLocaleString()} F</span>}
                                    {mRestauration > 0 && <span style={badgeVentilation('#d97706', '#fffbeb')}>🍽️ Restauration : {mRestauration.toLocaleString()} F</span>}
                                    {mExtras > 0 && <span style={badgeVentilation('#7c3aed', '#f5f3ff')}>⭐ Extras : {mExtras.toLocaleString()} F</span>}
                                  </div>

                                  {loadingDetail ? (
                                    <div style={{ textAlign: 'center', padding: '30px', color: '#64748b', fontSize: '13px' }}>Chargement des détails des factures...</div>
                                  ) : detailData ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                      
                                      {/* 1. BLOC HÉBERGEMENT */}
                                      {detailData.hebergement && detailData.hebergement.facture > 0 && (
                                        <div style={blocStyle}>
                                          <div style={blocHeaderStyle('#2563eb')}>
                                            <span>🛏️ HÉBERGEMENT</span>
                                            <span>Facturé : {detailData.hebergement.facture.toLocaleString()} F | Payé : {detailData.hebergement.paye.toLocaleString()} F | Reste : {detailData.hebergement.reste.toLocaleString()} F</span>
                                          </div>
                                          <div style={blocBodyStyle}>
                                            {detailData.hebergement.lignes.map((l, i) => (
                                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                <span>Chambre <strong>{l.chambre_numero || 'N/A'}</strong> ({l.nombre_nuites || 1} nuits du {l.date_check_in?.split('T')[0]} au {l.date_check_out?.split('T')[0]})</span>
                                                <span style={{ fontWeight: '800' }}>{Number(l.montant_ligne || 0).toLocaleString()} F</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* 2. BLOC RESTAURATION (Dynamique) */}
                                      {detailData.restauration && detailData.restauration.facture > 0 && (
                                        <div style={blocStyle}>
                                          <div style={blocHeaderStyle('#d97706')}>
                                            <span>🍽️ RESTAURATION</span>
                                            <span>Facturé : {detailData.restauration.facture.toLocaleString()} F | Payé : {detailData.restauration.paye.toLocaleString()} F | Reste : {detailData.restauration.reste.toLocaleString()} F</span>
                                          </div>
                                          <div style={blocBodyStyle}>
                                            {detailData.restauration.ventes.map((v, i) => (
                                              <div key={i} style={{ marginBottom: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: '800', color: '#334155' }}>Vente #{v.id} ({v.statut_vente})</div>
                                                {v.items.map((item, j) => (
                                                  <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', paddingLeft: '10px' }}>
                                                    <span>{item.libelle_produit || item.nom} (×{item.quantite})</span>
                                                    <span>{Number(item.montant_ttc_ligne || item.montant_total || 0).toLocaleString()} F</span>
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* 3. BLOC EXTRAS (Dynamique) */}
                                      {detailData.extras && detailData.extras.facture > 0 && (
                                        <div style={blocStyle}>
                                          <div style={blocHeaderStyle('#7c3aed')}>
                                            <span>⭐ EXTRAS & SERVICES</span>
                                            <span>Facturé : {detailData.extras.facture.toLocaleString()} F | Payé : {detailData.extras.paye.toLocaleString()} F | Reste : {detailData.extras.reste.toLocaleString()} F</span>
                                          </div>
                                          <div style={blocBodyStyle}>
                                            {detailData.extras.consommations.map((ext, i) => (
                                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                <span>{ext.nom_service || 'Service extra'} (×{ext.quantite || 1})</span>
                                                <span style={{ fontWeight: '800' }}>{Number(ext.montant_ttc || 0).toLocaleString()} F</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                    </div>
                                  ) : null}

                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'factures' && (
            <div style={contentCardStyle}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={thStyle}>Dossier / Ref</th>
                    <th style={thStyle}>Client / Réservant</th>
                    <th style={thStyle}>Période</th>
                    <th style={thStyle}>Montant / Reste</th>
                    <th style={thStyle}>Statut Paiement</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Action / Encaisser</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Chargement en cours...</td></tr>
                  ) : filteredSejours.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Aucun dossier ou facture trouvé.</td></tr>
                  ) : (
                    filteredSejours.map((s, idx) => {
                      const resteAPayer = Number(s.reste_a_payer || 0);
                      const estSolde = resteAPayer <= 0;
                      return (
                        <tr key={s.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ ...tdStyle, fontWeight: '800' }}>#{String(s.id).substring(0, 8)}</td>
                          <td style={tdStyle}>{s.reservant_nom || s.client_principal_nom || 'Client'}</td>
                          <td style={tdStyle}>{s.date_arrivee_prevue || '---'} au {s.date_depart_prevue || '---'}</td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: '800' }}>{Number(s.montant_total_groupe || 0).toLocaleString()} F</div>
                            <div style={{ fontSize: '11px', color: estSolde ? '#15803d' : '#b45309' }}>
                              Reste : {resteAPayer.toLocaleString()} F
                            </div>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ 
                              padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900',
                              background: estSolde ? '#dcfce7' : '#fef3c7',
                              color: estSolde ? '#15803d' : '#b45309'
                            }}>
                              {estSolde ? 'SOLDÉ' : 'NON SOLDÉ'}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <button 
                              onClick={() => navigate(`/admin/hebergement/checkout-facturation?reservation_id=${s.id}`)}
                              style={{
                                background: estSolde ? '#475569' : '#2563eb', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px',
                                fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px'
                              }}
                            >
                              {estSolde ? 'Voir Facture' : 'Vérifier & Encaisser'} <ArrowRight size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </main>

      <style>{`@keyframes slideInToast { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
};

const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const badgeVentilation = (color, bg) => ({ padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', color: color, background: bg, border: `1px solid ${color}33` });
const blocStyle = { border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' };
const blocHeaderStyle = (color) => ({ background: '#f8fafc', borderLeft: `4px solid ${color}`, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', fontWeight: '900', color: '#1e293b' });
const blocBodyStyle = { padding: '10px 12px', background: 'white' };
const badgeStyle = (mode) => {
  let bg = '#dcfce7', color = '#15803d';
  if (mode === 'TMONEY' || mode === 'FLOOZ') { bg = '#e0f2fe'; color = '#0369a1'; }
  else if (mode === 'CARTE BANCAIRE') { bg = '#fef3c7'; color = '#b45309'; }
  return { padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900', background: bg, color: color };
};
const toastContainerStyle = { position: 'fixed', top: '20px', right: '20px', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

export default EncaissementHebergement;